import { useEffect, useState } from "react";
import { Loader2, BookOpen, CheckCircle2, Sparkles, NotebookPen } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { fetchMyStats } from "@/lib/inmindApi";
import { categoryLabel } from "@/components/CategoryBadge";

export default function StatsPage() {
  const [s, setS] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchMyStats().then(setS).finally(() => setLoading(false));
  }, []);

  if (loading || !s) {
    return (
      <div className="flex items-center justify-center py-24 text-[var(--inmind-muted)]">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
      </div>
    );
  }

  const cats = Object.entries(s.by_category || {});
  const max = Math.max(1, ...cats.map(([, v]) => v));

  return (
    <div className="space-y-5" data-testid="stats-page">
      <div>
        <div className="text-[10px] tracking-[0.22em] uppercase font-semibold text-[var(--inmind-muted)]">
          Your record
        </div>
        <h2 className="font-serif text-2xl sm:text-3xl mt-1 text-[var(--inmind-text)] tracking-tight">
          A quiet log of what you've held
        </h2>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Tile icon={BookOpen} label="Seen" value={s.total} testId="stat-total" />
        <Tile icon={CheckCircle2} label="Reviewed" value={s.reviewed} testId="stat-reviewed" />
        <Tile icon={NotebookPen} label="Notes" value={s.notes} testId="stat-notes" />
        <Tile icon={Sparkles} label="Think messages" value={s.ai_messages} testId="stat-ai" />
      </div>

      <Card className="rounded-2xl border border-black/5 bg-white shadow-[0_4px_18px_rgba(0,0,0,0.03)]">
        <CardContent className="p-5">
          <div className="text-[10px] tracking-[0.22em] uppercase font-semibold text-[var(--inmind-muted)] mb-3">
            By subject
          </div>
          <ul className="space-y-3" data-testid="stat-by-cat">
            {cats.map(([k, v]) => (
              <li key={k}>
                <div className="flex items-center justify-between text-sm mb-1">
                  <span className="text-[var(--inmind-text)]">{categoryLabel(k)}</span>
                  <span className="tabular-nums text-[var(--inmind-text-secondary)]">{v}</span>
                </div>
                <div className="h-1.5 bg-black/5 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-[var(--inmind-primary)] transition-all"
                    style={{ width: `${(v / max) * 100}%` }}
                  />
                </div>
              </li>
            ))}
          </ul>
        </CardContent>
      </Card>
    </div>
  );
}

function Tile({ icon: Icon, label, value, testId }) {
  return (
    <Card className="rounded-2xl border border-black/5 bg-white shadow-[0_4px_18px_rgba(0,0,0,0.03)]">
      <CardContent className="p-4">
        <Icon className="w-4 h-4 text-[var(--inmind-primary)]" strokeWidth={1.6} />
        <div className="font-serif text-3xl mt-2 text-[var(--inmind-text)] tabular-nums" data-testid={testId}>{value}</div>
        <div className="text-[10px] tracking-[0.18em] uppercase font-semibold text-[var(--inmind-muted)] mt-1">{label}</div>
      </CardContent>
    </Card>
  );
}
